function initializePageBanner() {
  const OFFSET_TOP = 95 + 32;
  const OFFSET_BOTTOM = 120;
  const BREAKPOINT = 992;

  // Background video: Edge/Chromium sometimes refuse to autoplay the muted
  // hero video even with the correct attributes (autoplay policy, efficiency
  // mode, or a preload race). Setting `muted` as a property — not just the
  // attribute — and explicitly calling play() is the reliable fix. Retry once
  // the video has data and again when the tab becomes visible.
  const bgVideo = document.querySelector(".page-banner__video");
  if (bgVideo) {
    const playVideo = function () {
      bgVideo.muted = true;
      const playback = bgVideo.play();
      if (playback && typeof playback.catch === "function") {
        playback.catch(function () {});
      }
    };

    playVideo();
    bgVideo.addEventListener("loadeddata", playVideo, { once: true });
    bgVideo.addEventListener("canplay", playVideo, { once: true });
    document.addEventListener("visibilitychange", function () {
      if (!document.hidden && bgVideo.paused) playVideo();
    });
  }

  const form = document.querySelector(".page-banner__form");
  if (!form) return;

  const main = document.querySelector("main");
  if (!main) return;

  // The form's resting top is anchored to the banner title so their top edges
  // line up, rather than to the form's own CSS position. Fall back to the form
  // itself if no title is present.
  const banner = form.closest(".page-banner");
  const title = (banner || document).querySelector(".page-banner__wrapper");
  const anchor = title || form;

  // On normal pages the banner is a direct child of the page content and the
  // blocks below it flow in the half column beside the form, so the form should
  // travel to the bottom of that last block.
  const bannerParent = banner ? banner.parentElement : null;
  const columnEnd =
    bannerParent &&
    bannerParent.id === "site-content" &&
    bannerParent.lastElementChild &&
    bannerParent.lastElementChild !== banner
      ? bannerParent.lastElementChild
      : null;

  // On single events/webinars the banner is nested inside .blog-post, above the
  // article body and a trailing share section. The form should stop at the
  // bottom of the article body (.blog-post__body) rather than travelling down
  // past the share section.
  const blogPost = banner ? banner.closest(".blog-post") : null;
  const blogPostBody = blogPost
    ? blogPost.querySelector(".blog-post__body")
    : null;

  // On desktop the form is taken out of the banner's layout and follows the
  // scroll like `position: sticky`, but pinned with JS so it can travel out of
  // the banner and down alongside the article. To avoid any visible "snap" we
  // never toggle between positioning modes: the form stays `position: fixed`
  // the whole time and its `top` is driven by one continuous formula, so there
  // is no discrete jump when it reaches the sticky offset.
  let enabled = window.innerWidth >= BREAKPOINT;
  let restingTopDoc = 0;
  let restingLeft = 0;
  let restingWidth = 0;
  let measured = false;

  // Read the same scroll value the rest of the site renders against. Lenis
  // smooth-scrolls in its own RAF loop, so reading `lenis.animatedScroll`
  // (instead of the frame-late native scroll position) keeps the form moving
  // in lockstep with the content.
  function getScroll() {
    return window.lenis ? window.lenis.animatedScroll : window.scrollY;
  }

  function reset() {
    form.style.position = "";
    form.style.top = "";
    form.style.left = "";
    form.style.width = "";
    form.style.maxHeight = "";
  }

  // The inner form scrolls when it is taller than the (viewport-capped) form.
  // Only lock the site's smooth scroll to it (`data-lenis-prevent`) while it is
  // actually scrollable, otherwise hovering a short form would stall the page.
  const formInner = form.querySelector(".page-banner__form-inner");
  function syncScrollLock() {
    if (!formInner) return;
    if (formInner.scrollHeight - formInner.clientHeight > 1) {
      formInner.setAttribute("data-lenis-prevent", "");
    } else {
      formInner.removeAttribute("data-lenis-prevent");
    }
  }

  // Read the form's natural geometry in its CSS resting (absolute) position.
  // Toggling back to that position and reading it happens synchronously inside
  // one task, so the browser never paints the intermediate state (no flicker).
  function measure() {
    if (!enabled) return;
    reset();
    const rect = form.getBoundingClientRect();
    // getBoundingClientRect() is viewport-relative, but `left` on the pinned
    // form is not affected by document scroll. Store the document x and take
    // the live scroll off again in update(), so a stray horizontal scroll --
    // the browser pulling a focused field into view, which body's
    // overflow-x: hidden does not prevent -- cannot be baked in here.
    restingLeft = rect.left + window.scrollX;
    restingWidth = rect.width;
    restingTopDoc = anchor.getBoundingClientRect().top + getScroll() + 32;
    measured = true;
  }

  function update() {
    if (!enabled || !measured) return;

    // Where the form would sit if it simply scrolled with the page.
    const naturalTop = restingTopDoc - getScroll();
    // Keep the viewport cap stable when parking at the column's bottom. If it
    // grows as the form moves up, the form's new height can cross that boundary.
    form.style.maxHeight =
      Math.max(0, window.innerHeight - Math.max(naturalTop, OFFSET_TOP) - 32) +
      "px";
    // Lowest the form may go before it parks. On normal pages this is the bottom
    // of the last block in the column; on event/webinar singles it is the bottom
    // of the article body so the form stops there and never overlaps the share
    // section below it. Falls back to the main element if neither is present.
    const formHeight = form.offsetHeight;
    let parkTop;
    if (columnEnd) {
      parkTop = columnEnd.getBoundingClientRect().bottom - formHeight;
    } else if (blogPostBody) {
      parkTop = blogPostBody.getBoundingClientRect().bottom - formHeight;
    } else {
      parkTop =
        main.getBoundingClientRect().bottom - OFFSET_BOTTOM - formHeight - 64;
    }
    // Continuous: scroll freely until OFFSET_TOP, hold there, then ride up to
    // park near the bottom. max()/min() of continuous terms => no snap.
    const top = Math.max(naturalTop, Math.min(OFFSET_TOP, parkTop));

    form.style.position = "fixed";
    form.style.top = top + "px";
    form.style.left = restingLeft - window.scrollX + "px";
    form.style.width = restingWidth + "px";
  }

  function refresh() {
    measure();
    update();
    syncScrollLock();
  }

  let connectedLenis = null;
  function connectSmoothScroll() {
    if (window.lenis && window.lenis !== connectedLenis) {
      if (connectedLenis) connectedLenis.off("scroll", update);
      window.lenis.on("scroll", update);
      connectedLenis = window.lenis;
    }
  }

  // Attach immediately: slow resources must not delay scrolling, and a script
  // released by consent after window.load must not miss its only listener.
  window.addEventListener("scroll", update, { passive: true });
  connectSmoothScroll();
  refresh();
  window.addEventListener("load", function () {
    connectSmoothScroll();
    refresh();
  }, { once: true });
  if (document.fonts) document.fonts.ready.then(refresh);

  window.addEventListener("resize", function () {
    enabled = window.innerWidth >= BREAKPOINT;
    if (!enabled) {
      reset();
      measured = false;
    } else {
      measure();
      update();
    }
    syncScrollLock();
  });

  // The embedded form (e.g. HubSpot) is injected asynchronously and keeps
  // changing size afterwards, so the scroll lock has to be kept in step with it.
  if (typeof ResizeObserver !== "undefined") {
    let raf = 0;
    let lastWidth = 0;

    const observer = new ResizeObserver(function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        // update() writes maxHeight on every scroll frame, which resizes the
        // form and notifies this observer in turn. Re-measuring from here
        // would un-pin and re-pin the form continuously while scrolling, so
        // any transient layout -- a HubSpot field re-rendering, a focus
        // scroll after picking an option -- gets baked into restingLeft and
        // throws the form sideways until the next measurement corrects it.
        //
        // None of the cached geometry depends on the form's height:
        // restingTopDoc is read from the anchor, and the form is out of flow
        // at rest, so only a width change needs a re-measure.
        const width = form.getBoundingClientRect().width;

        if (width !== lastWidth) {
          lastWidth = width;
          measure();
        }

        // Async form content can change its height while parked at the bottom.
        update();
        // The scroll lock does depend on height, so it is checked either way.
        syncScrollLock();
      });
    });
    observer.observe(form);
    if (formInner) observer.observe(formInner);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePageBanner, {
    once: true,
  });
} else {
  initializePageBanner();
}
